/**
 * The Matrix as one doc per record (S305, 2026-10-06; engine:
 * `sync/recordDocSync.ts`).
 *
 *   matrix-device-<id>                one device
 *   matrix-slot-<kind>-<ch>-<number>  one MIDI signal and its claims
 *
 * A slot's doc id is the SIGNAL (`slotKey`), not the slot's ULID: two PCs that
 * each claim CC 20 on channel 3 write the same doc, so the signal stays one
 * slot everywhere instead of arriving as two slots that clash. Note labels go
 * through `idPart` ("F#4" is not a legal doc id).
 *
 * Order: a placed record keeps its position, a new one is appended. Two PCs
 * can therefore list devices in a different order; the matrix view is a grid
 * by channel and number, so only the device list would show it.
 */

import type { MatrixDevice, MatrixLibrary, MatrixSlot } from '@opbuttons/schema';
import { idPart, type RecordAdapter } from '../sync/recordDocSync.ts';

const DEVICE = 'matrix-device-';
const SLOT = 'matrix-slot-';

export function deviceDocId(id: string): string { return `${DEVICE}${idPart(id)}`; }
export function slotDocId(s: Pick<MatrixSlot, 'kind' | 'channel' | 'ccNumber' | 'noteLabel' | 'pcNumber'>): string {
  const n = s.kind === 'cc' ? String(s.ccNumber ?? '?') : s.kind === 'pc' ? String(s.pcNumber ?? '?') : (s.noteLabel ?? '?').toUpperCase();
  return `${SLOT}${s.kind}-${s.channel}-${idPart(n)}`;
}

function isDevice(r: unknown): r is MatrixDevice {
  const d = r as MatrixDevice;
  return !!d && typeof d === 'object' && typeof d.id === 'string' && typeof d.name === 'string';
}
function isSlot(r: unknown): r is MatrixSlot {
  const s = r as MatrixSlot;
  return !!s && typeof s === 'object' && typeof s.id === 'string' && (s.kind === 'cc' || s.kind === 'note' || s.kind === 'pc')
    && typeof s.channel === 'number' && Array.isArray(s.claims);
}

function placeAll<T>(list: T[], docIdOf: (x: T) => string, puts: Map<string, unknown>, dels: Set<string>, prefix: string): T[] {
  const out: T[] = [];
  const placed = new Set<string>();
  for (const x of list) {
    const id = docIdOf(x);
    if (dels.has(id)) continue;
    if (puts.has(id)) { out.push(puts.get(id) as T); placed.add(id); } else out.push(x);
  }
  for (const [id, r] of puts) if (id.startsWith(prefix) && !placed.has(id)) out.push(r as T);
  return out;
}

export const matrixAdapter: RecordAdapter<MatrixLibrary> = {
  label: 'Matrix',
  prefixes: [DEVICE, SLOT],
  legacyDocId: 'matrix',
  split(lib) {
    const out = new Map<string, unknown>();
    for (const d of lib.devices) out.set(deviceDocId(d.id), d);
    for (const s of lib.slots) out.set(slotDocId(s), s);
    return out;
  },
  apply(lib, puts, dels) {
    return {
      ...lib,
      devices: placeAll(lib.devices, (d) => deviceDocId(d.id), puts, dels, DEVICE),
      slots: placeAll(lib.slots, slotDocId, puts, dels, SLOT),
    };
  },
  problem(docId, r) {
    if (docId.startsWith(DEVICE)) {
      if (!isDevice(r)) return 'not a matrix device';
      return deviceDocId(r.id) === docId ? null : 'device id does not match its document';
    }
    if (!isSlot(r)) return 'not a matrix slot';
    return slotDocId(r) === docId ? null : 'slot signal does not match its document';
  },
  legacyLibrary(body) {
    const lib = (body as { library?: MatrixLibrary } | null)?.library;
    return lib && Array.isArray(lib.devices) && Array.isArray(lib.slots) ? lib : null;
  },
};
